import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, ArrowLeft, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", amount: 0, currency: "KES", description: "", active: true, order: 0 };

export default function RateCards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const isAdmin = ["admin", "global_admin"].includes(user.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/housing/rate-cards"); setItems(data); }
    catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = {
        ...editing,
        amount: Number(editing.amount) || 0,
        order: Number(editing.order) || 0,
      };
      if (editing.id) await api.put(`/housing/rate-cards/${editing.id}`, payload);
      else await api.post("/housing/rate-cards", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (rc) => {
    if (!window.confirm(`Deactivate "${rc.name}"? Existing bookings keep their snapshot rate.`)) return;
    try { await api.delete(`/housing/rate-cards/${rc.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="rate-cards-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Rate Cards</h1>
          <p className="text-[#525C58] mt-1">Named nightly rates that any housing unit can charge. Coordinators pick one per booking segment.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setEditing({ ...blank, order: items.length + 1 })} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-rate-card-btn">
            <Plus className="h-4 w-4 mr-2" /> New rate card
          </Button>
        )}
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Tag className="h-5 w-5 text-[#40916C]" /> Rate cards ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <div className="py-10 text-center text-[#7A8A84]">Loading…</div> : items.length === 0 ? (
            <p className="text-[#7A8A84] text-center py-10">
              No rate cards yet. Create your first — e.g. "Mission Partner" at KES 1,500/night.
            </p>
          ) : (
            <Table data-testid="rate-cards-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Nightly rate</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(rc => (
                  <TableRow key={rc.id} data-testid={`rate-card-row-${rc.id}`} className={rc.active ? "" : "opacity-60"}>
                    <TableCell className="text-[#7A8A84] tabular-nums">{rc.order || "—"}</TableCell>
                    <TableCell className="font-medium text-[#1C1F1E]">{rc.name}</TableCell>
                    <TableCell className="text-sm text-[#525C58]">{rc.description || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {rc.currency} {Number(rc.amount).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {rc.active ? <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Active</Badge>
                                   : <Badge className="bg-red-100 text-red-800 border-0 text-[10px]">Inactive</Badge>}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ ...rc })} className="text-[#1B4332]" data-testid={`edit-rate-${rc.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {rc.active && (
                          <Button size="sm" variant="ghost" onClick={() => remove(rc)} className="text-red-600" data-testid={`delete-rate-${rc.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit rate card" : "New rate card"}</DialogTitle>
            <DialogDescription>This rate becomes available in every booking segment dropdown.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Mission Partner — Single" data-testid="rate-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={editing.currency} onValueChange={(v) => setEditing({ ...editing, currency: v })}>
                    <SelectTrigger data-testid="rate-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KES">KES</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount per night *</Label>
                  <Input type="number" min={0} value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="rate-amount-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Optional — e.g. for partnering agencies sponsoring a clinical visit" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Display order</Label>
                  <Input type="number" min={0} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                </div>
                <div className="flex items-center gap-2 pt-7">
                  <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-rate-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
